/**
 * Month Flow — the forward-looking view.
 *
 * The Overview tab answers "where did the money go". This one answers "does
 * next month work": what lands on which day, and how low the balance gets in
 * between. Those are different questions and they need different data —
 * everything here is a projection from things that repeat, not a record of
 * things that happened.
 *
 * Three sources, drawn together for the first time:
 *   · household bills and manual income   (recurring_money — editable here)
 *   · recurring invoices and bills        (repeating_invoices — read-only)
 *   · Stripe plans                        (crm_subscriptions — read-only)
 *
 * Everything recomputes from a single pure pass (utils/rhythm.ts), which is
 * what lets the what-if toggles respond instantly instead of round-tripping.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  MoneySource,
  RecurringMoneyItem,
  RhythmFilters,
  RhythmOccurrence,
  RhythmScenario,
  TxScope,
} from '../../types';
import { useReconciliation } from '../../contexts/ReconciliationContext';
import { useRecurringMoney } from '../../contexts/RecurringMoneyContext';
import { getCategoryColour, getCategoryLabel, SEED_CATEGORIES } from '../../utils/categories';
import {
  SCOPE_LABELS,
  SOURCE_COLOURS,
  SOURCE_LABELS,
  addDays,
  buildRhythm,
  describeSchedule,
  isDayString,
  monthBounds,
  monthlyEquivalent,
} from '../../utils/rhythm';
import { daysToCsv, downloadCsv, itemsToCsv, occurrencesToCsv } from '../../utils/rhythmImport';
import { downloadMonthFlowWorkbook } from '../../utils/monthFlowWorkbook';
import { RhythmChart } from './RhythmChart';
import { RecurringItemModal } from './RecurringItemModal';
import { ImportItemsModal } from './ImportItemsModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const money = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n);

const moneyShort = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);

/** Today in the browser's calendar, built field-by-field. `toISOString()` would
 *  give the UTC day, which is yesterday for most of an Adelaide morning. */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const weekday = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
  return `${WEEKDAYS_SHORT[weekday]} ${d} ${MONTHS_SHORT[m - 1]}`;
}

/** Shift a month range by whole months, keeping it aligned to month bounds. */
function shiftMonths(day: string, delta: number): string {
  const [y, m] = day.split('-').map(Number);
  const abs = y * 12 + (m - 1) + delta;
  return `${String(Math.floor(abs / 12)).padStart(4, '0')}-${String((abs % 12) + 1).padStart(2, '0')}-01`;
}

/** True when the range is exactly one calendar month — decides whether the
 *  header shows "August 2026" or a date range. */
function isWholeMonth(start: string, end: string): boolean {
  const b = monthBounds(start);
  return b.start === start && b.end === end;
}

function rangeHeading(start: string, end: string): string {
  if (!isDayString(start) || !isDayString(end)) return 'Pick a range';
  if (isWholeMonth(start, end)) {
    const [y, m] = start.split('-').map(Number);
    return `${MONTHS_LONG[m - 1]} ${y}`;
  }
  return `${formatDay(start)} — ${formatDay(end)}`;
}

type Preset = 'this_month' | 'next_month' | 'next_3' | 'next_12' | 'custom';

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------

function SummaryCard({
  label,
  value,
  tone,
  sub,
  icon,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'danger' | 'warning' | 'muted';
  sub?: React.ReactNode;
  icon?: string;
}) {
  const toneClass =
    tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : '';
  return (
    <div className="col-6 col-xl-3">
      <div className="card h-100">
        <div className="card-body p-4">
          <div className="text-muted fs-8 text-uppercase fw-semibold d-flex align-items-center gap-1">
            {icon && <i className={`bi ${icon}`} />}
            {label}
          </div>
          <div className={`fs-2 fw-bold ${toneClass}`}>{value}</div>
          {sub && <div className="text-muted fs-8">{sub}</div>}
        </div>
      </div>
    </div>
  );
}

/** A toggle chip. Empty filter set = everything on, so "active" means either
 *  nothing is selected or this one is. */
function Chip({
  label,
  active,
  colour,
  onClick,
  count,
}: {
  label: string;
  active: boolean;
  colour?: string;
  onClick: () => void;
  count?: number;
}) {
  return (
    <button
      type="button"
      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-light'} d-inline-flex align-items-center gap-1`}
      onClick={onClick}
    >
      {colour && (
        <span className="rounded-circle d-inline-block" style={{ width: 8, height: 8, background: colour }} />
      )}
      {label}
      {count !== undefined && <span className="opacity-75 fs-8">({count})</span>}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function MonthFlowView() {
  const reconciliation = useReconciliation();
  const { bills, asOf } = reconciliation.data;
  const {
    items, templates, subscriptions, loading, error, sourceErrors, refresh,
    setActive,
  } = useRecurringMoney();

  const [searchParams, setSearchParams] = useSearchParams();

  // --- range ---------------------------------------------------------------
  const initialBounds = monthBounds(today());
  const [rangeStart, setRangeStart] = useState(() => {
    const p = searchParams.get('from');
    return p && isDayString(p) ? p : initialBounds.start;
  });
  const [rangeEnd, setRangeEnd] = useState(() => {
    const p = searchParams.get('to');
    return p && isDayString(p) ? p : initialBounds.end;
  });
  const [preset, setPreset] = useState<Preset>('this_month');

  // --- scenario + balance --------------------------------------------------
  const [scenario, setScenario] = useState<RhythmScenario>(
    searchParams.get('scenario') === 'expected' ? 'expected' : 'full'
  );
  const [openingBalanceText, setOpeningBalanceText] = useState(
    () => localStorage.getItem('monthflow.openingBalance') || '0'
  );
  const [bufferText, setBufferText] = useState(() => localStorage.getItem('monthflow.buffer') || '');
  const [showBalance, setShowBalance] = useState(true);

  // --- filters -------------------------------------------------------------
  const [directions, setDirections] = useState<Set<'in' | 'out'>>(new Set());
  const [scopes, setScopes] = useState<Set<TxScope>>(new Set());
  const [sources, setSources] = useState<Set<MoneySource>>(new Set());
  const [categories, setCategories] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [mutedIds, setMutedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // --- modals --------------------------------------------------------------
  const [showItemModal, setShowItemModal] = useState(false);
  const [editing, setEditing] = useState<RecurringMoneyItem | null>(null);
  const [showImport, setShowImport] = useState(false);

  // Keep the range and scenario in the URL so a view can be linked and
  // survives a refresh. `view` belongs to the page above, so it's preserved
  // rather than rewritten.
  //
  // The functional updater matters here: this effect only depends on the range
  // and scenario, so a `searchParams` captured in the closure can be a render
  // behind. Building the next set from the CURRENT one instead means a param
  // the page above just wrote can't be clobbered.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'monthflow');
        next.set('from', rangeStart);
        next.set('to', rangeEnd);
        if (scenario === 'expected') next.set('scenario', 'expected');
        else next.delete('scenario');
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeStart, rangeEnd, scenario]);

  useEffect(() => {
    localStorage.setItem('monthflow.openingBalance', openingBalanceText);
  }, [openingBalanceText]);
  useEffect(() => {
    localStorage.setItem('monthflow.buffer', bufferText);
  }, [bufferText]);

  const openingBalance = useMemo(() => {
    const n = Number(openingBalanceText.replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }, [openingBalanceText]);

  const bufferLine = useMemo(() => {
    if (!bufferText.trim()) return null;
    const n = Number(bufferText.replace(/[$,\s]/g, ''));
    return Number.isFinite(n) ? n : null;
  }, [bufferText]);

  const applyPreset = useCallback((p: Preset) => {
    setPreset(p);
    const t = today();
    if (p === 'this_month') {
      const b = monthBounds(t);
      setRangeStart(b.start);
      setRangeEnd(b.end);
    } else if (p === 'next_month') {
      const start = shiftMonths(t, 1);
      setRangeStart(start);
      setRangeEnd(monthBounds(start).end);
    } else if (p === 'next_3') {
      const b = monthBounds(t);
      setRangeStart(b.start);
      setRangeEnd(monthBounds(shiftMonths(t, 2)).end);
    } else if (p === 'next_12') {
      const b = monthBounds(t);
      setRangeStart(b.start);
      setRangeEnd(monthBounds(shiftMonths(t, 11)).end);
    }
  }, []);

  /** Month arrows. When the range is a whole month they step a month; when it
   *  isn't, they slide the window by its own length so a custom range stays
   *  the same size. */
  const stepRange = useCallback(
    (delta: number) => {
      setPreset('custom');
      if (isWholeMonth(rangeStart, rangeEnd)) {
        const start = shiftMonths(rangeStart, delta);
        setRangeStart(start);
        setRangeEnd(monthBounds(start).end);
      } else {
        const span = Math.round(
          (new Date(`${rangeEnd}T12:00:00Z`).getTime() - new Date(`${rangeStart}T12:00:00Z`).getTime()) / 86400000
        );
        setRangeStart(addDays(rangeStart, delta * (span + 1)));
        setRangeEnd(addDays(rangeEnd, delta * (span + 1)));
      }
    },
    [rangeStart, rangeEnd]
  );

  // --- the one computation -------------------------------------------------
  const filters: RhythmFilters = useMemo(
    () => ({
      rangeStart, rangeEnd, scenario, directions, scopes, sources, categories,
      search, mutedIds, openingBalance,
    }),
    [rangeStart, rangeEnd, scenario, directions, scopes, sources, categories, search, mutedIds, openingBalance]
  );

  const result = useMemo(
    () => buildRhythm({ items, templates, subscriptions, bills, asOf }, filters),
    [items, templates, subscriptions, bills, asOf, filters]
  );

  /** Unfiltered totals, so the chips can show what each one would bring back
   *  rather than only what's already visible.
   *
   *  Deliberately NOT keyed on `filters` — that object changes on every search
   *  keystroke, and re-projecting a 12-month range per character is exactly
   *  the kind of thing that makes a search box feel broken. The only inputs
   *  that can change this result are the range, the scenario and the mutes. */
  const unfiltered = useMemo(
    () =>
      buildRhythm(
        { items, templates, subscriptions, bills, asOf },
        {
          rangeStart, rangeEnd, scenario, openingBalance, mutedIds,
          directions: new Set(), scopes: new Set(), sources: new Set(), categories: new Set(), search: '',
        }
      ),
    [items, templates, subscriptions, bills, asOf, rangeStart, rangeEnd, scenario, openingBalance, mutedIds]
  );

  const activeCategories = useMemo(() => {
    const ids = new Set(unfiltered.occurrences.map((o) => o.category));
    return SEED_CATEGORIES.filter((c) => ids.has(c.id));
  }, [unfiltered.occurrences]);

  const sourceCounts = useMemo(() => {
    const counts = new Map<MoneySource, number>();
    for (const o of unfiltered.occurrences) counts.set(o.source, (counts.get(o.source) || 0) + 1);
    return counts;
  }, [unfiltered.occurrences]);

  const daysWithActivity = useMemo(() => result.days.filter((d) => d.occurrences.length > 0), [result.days]);

  const filterCount =
    directions.size + scopes.size + sources.size + categories.size + (search ? 1 : 0) + mutedIds.size;

  const toggleIn = <T,>(set: Set<T>, value: T, setter: (s: Set<T>) => void) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const clearFilters = () => {
    setDirections(new Set());
    setScopes(new Set());
    setSources(new Set());
    setCategories(new Set());
    setSearch('');
    setMutedIds(new Set());
  };

  const toggleMute = (id: string) => toggleIn(mutedIds, id, setMutedIds);

  // --- export --------------------------------------------------------------
  /** The complete picture as a styled workbook: all three groups, the summary,
   *  and the day-by-day balance on one sheet. Ignores the on-screen chips on
   *  purpose — see monthFlowWorkbook.ts. */
  const exportWorkbook = () => {
    if (items.length === 0 && templates.length === 0 && subscriptions.length === 0) {
      toast.info('Nothing to export yet — add or import some recurring items first.');
      return;
    }
    try {
      const filename = downloadMonthFlowWorkbook({
        items,
        templates,
        subscriptions, bills, asOf,
        rangeStart,
        rangeEnd,
        scenario,
        openingBalance,
        bufferLine,
        mutedIds,
      });
      toast.success(`Exported ${filename}`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not build the spreadsheet');
    }
  };

  const exportOccurrences = () => {
    if (result.occurrences.length === 0) {
      toast.info('Nothing in this range to export.');
      return;
    }
    downloadCsv(`month-flow-${rangeStart}-to-${rangeEnd}.csv`, occurrencesToCsv(result.occurrences));
  };
  const exportDays = () => {
    if (result.days.length === 0) {
      toast.info('Nothing in this range to export.');
      return;
    }
    downloadCsv(`month-flow-balance-${rangeStart}-to-${rangeEnd}.csv`, daysToCsv(result.days));
  };
  const exportItems = () => {
    if (items.length === 0) {
      toast.info('No recurring items to export yet.');
      return;
    }
    downloadCsv('recurring-items.csv', itemsToCsv(items));
  };

  // --- item list -----------------------------------------------------------
  /** Manual items with their monthly-equivalent cost, biggest first. The
   *  normalisation matters: a $26.53 weekly debit is $115/month, and a list
   *  sorted on the raw figure buries it below a $75 monthly one. */
  const rankedItems = useMemo(() => {
    return items
      .map((it) => ({ item: it, monthly: monthlyEquivalent(it, scenario) }))
      .sort((a, b) => b.monthly - a.monthly);
  }, [items, scenario]);

  const openAdd = () => {
    setEditing(null);
    setShowItemModal(true);
  };
  const openEdit = (item: RecurringMoneyItem) => {
    setEditing(item);
    setShowItemModal(true);
  };

  /** Promote a session mute into a persisted "off". The mute is dropped at the
   *  same time — leaving both on would mean unticking `active` later had no
   *  visible effect, because the mute would still be hiding it. */
  const persistOff = async (item: RecurringMoneyItem) => {
    try {
      await setActive(item.id, false);
      setMutedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      toast.success(`${item.name} is no longer counted`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not turn that off');
    }
  };

  const persistOn = async (item: RecurringMoneyItem) => {
    try {
      await setActive(item.id, true);
      toast.success(`${item.name} is back in the forecast`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not turn that on');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <span className="spinner-border text-primary" role="status" />
        <div className="text-muted mt-3">Loading your recurring money…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex justify-content-between align-items-center">
        <span>{error}</span>
        <button className="btn btn-sm btn-light" onClick={refresh}>
          Try again
        </button>
      </div>
    );
  }

  const nothingYet = items.length === 0 && templates.length === 0 && subscriptions.length === 0;

  return (
    <div className="month-flow">
      {reconciliation.error && <div role="alert" className="alert alert-warning">Bill balances could not be loaded. This forecast is incomplete. <button className="btn btn-sm btn-light" onClick={() => reconciliation.refresh()}>Retry</button></div>}
      {/* ------------------------------------------------ header ---------- */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-icon btn-sm btn-light" onClick={() => stepRange(-1)} aria-label="Previous period">
            <i className="bi bi-chevron-left" />
          </button>
          <h3 className="mb-0 mx-1" style={{ minWidth: 180 }}>
            {rangeHeading(rangeStart, rangeEnd)}
          </h3>
          <button className="btn btn-icon btn-sm btn-light" onClick={() => stepRange(1)} aria-label="Next period">
            <i className="bi bi-chevron-right" />
          </button>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="btn-group btn-group-sm">
            {([
              ['this_month', 'This month'],
              ['next_month', 'Next month'],
              ['next_3', '3 months'],
              ['next_12', '12 months'],
            ] as Array<[Preset, string]>).map(([value, label]) => (
              <button
                key={value}
                className={`btn ${preset === value ? 'btn-primary' : 'btn-light'}`}
                onClick={() => applyPreset(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <button className="btn btn-sm btn-light" onClick={() => setShowImport(true)}>
            <i className="bi bi-upload me-1" /> Import
          </button>

          <div className="dropdown">
            <button className="btn btn-sm btn-light dropdown-toggle" data-bs-toggle="dropdown">
              <i className="bi bi-download me-1" /> Export
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button className="dropdown-item" onClick={exportWorkbook}>
                  <span className="fw-semibold">
                    <i className="bi bi-file-earmark-spreadsheet text-success me-1" />
                    Excel workbook
                  </span>
                  <div className="text-muted fs-8">
                    Everything on one styled sheet — personal, business and income
                  </div>
                </button>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <h6 className="dropdown-header">CSV — exactly what's on screen</h6>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportOccurrences}>
                  Every movement in view
                  <div className="text-muted fs-8">One row per dated payment</div>
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={exportDays}>
                  Day-by-day balance
                  <div className="text-muted fs-8">The chart, as numbers</div>
                </button>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <button className="dropdown-item" onClick={exportItems}>
                  My recurring items
                  <div className="text-muted fs-8">Edit in a spreadsheet and re-import</div>
                </button>
              </li>
            </ul>
          </div>

          <button className="btn btn-sm btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-lg me-1" /> Add item
          </button>
        </div>
      </div>

      {sourceErrors.length > 0 && (
        <div
          className={`alert py-2 px-3 d-flex justify-content-between align-items-center ${
            sourceErrors.some((e) => e.retryable) ? 'alert-warning' : 'alert-light border'
          }`}
        >
          <div>
            {sourceErrors.map((e) => (
              <div key={e.message}>
                <i className={`bi ${e.retryable ? 'bi-exclamation-triangle' : 'bi-info-circle'} me-1`} />
                {e.message}
              </div>
            ))}
          </div>
          {sourceErrors.some((e) => e.retryable) && (
            <button className="btn btn-sm btn-light" onClick={refresh}>
              Retry
            </button>
          )}
        </div>
      )}

      {/* ------------------------------------------------ controls -------- */}
      <div className="card mb-4">
        <div className="card-body p-3 d-flex flex-wrap align-items-end gap-3">
          <div>
            <label className="form-label fs-8 text-muted text-uppercase fw-semibold mb-1">Starting balance</label>
            <div className="input-group input-group-sm" style={{ width: 150 }}>
              <span className="input-group-text">$</span>
              <input
                className="form-control"
                value={openingBalanceText}
                onChange={(e) => setOpeningBalanceText(e.target.value)}
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="form-label fs-8 text-muted text-uppercase fw-semibold mb-1">
              Buffer <span className="text-muted fw-normal">(optional)</span>
            </label>
            <div className="input-group input-group-sm" style={{ width: 150 }}>
              <span className="input-group-text">$</span>
              <input
                className="form-control"
                value={bufferText}
                placeholder="none"
                onChange={(e) => setBufferText(e.target.value)}
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="form-label fs-8 text-muted text-uppercase fw-semibold mb-1">Amounts</label>
            <div className="btn-group btn-group-sm d-block">
              <button
                className={`btn ${scenario === 'full' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setScenario('full')}
                title="The full billed amount for every item"
              >
                Full
              </button>
              <button
                className={`btn ${scenario === 'expected' ? 'btn-primary' : 'btn-light'}`}
                onClick={() => setScenario('expected')}
                title="The realistic figure where you've set one — the 'usually' column"
              >
                Usually
              </button>
            </div>
          </div>

          <div>
            <label className="form-label fs-8 text-muted text-uppercase fw-semibold mb-1">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={rangeStart}
              onChange={(e) => {
                if (isDayString(e.target.value)) {
                  setRangeStart(e.target.value);
                  setPreset('custom');
                }
              }}
            />
          </div>
          <div>
            <label className="form-label fs-8 text-muted text-uppercase fw-semibold mb-1">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={rangeEnd}
              onChange={(e) => {
                if (isDayString(e.target.value)) {
                  setRangeEnd(e.target.value);
                  setPreset('custom');
                }
              }}
            />
          </div>

          <div className="ms-auto d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="mf-balance"
                checked={showBalance}
                onChange={(e) => setShowBalance(e.target.checked)}
              />
              <label className="form-check-label fs-8" htmlFor="mf-balance">
                Balance line
              </label>
            </div>
            <button
              className={`btn btn-sm ${filterCount > 0 ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setShowFilters((s) => !s)}
            >
              <i className="bi bi-funnel me-1" />
              Filters
              {filterCount > 0 && <span className="badge badge-circle badge-light ms-1">{filterCount}</span>}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="card-body border-top p-3 pt-3">
            <div className="row g-3">
              <div className="col-12 col-lg-4">
                <div className="fs-8 text-muted text-uppercase fw-semibold mb-1">Direction</div>
                <div className="d-flex flex-wrap gap-1">
                  <Chip label="Money in" colour="#10b981" active={directions.size === 0 || directions.has('in')} onClick={() => toggleIn(directions, 'in', setDirections)} />
                  <Chip label="Money out" colour="#ef4444" active={directions.size === 0 || directions.has('out')} onClick={() => toggleIn(directions, 'out', setDirections)} />
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="fs-8 text-muted text-uppercase fw-semibold mb-1">Books</div>
                <div className="d-flex flex-wrap gap-1">
                  {(['personal', 'business'] as TxScope[]).map((s) => (
                    <Chip
                      key={s}
                      label={SCOPE_LABELS[s]}
                      active={scopes.size === 0 || scopes.has(s)}
                      onClick={() => toggleIn(scopes, s, setScopes)}
                    />
                  ))}
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="fs-8 text-muted text-uppercase fw-semibold mb-1">Search</div>
                <input
                  className="form-control form-control-sm"
                  placeholder="Name, customer, note…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="col-12">
                <div className="fs-8 text-muted text-uppercase fw-semibold mb-1">Where it comes from</div>
                <div className="d-flex flex-wrap gap-1">
                  {(Object.keys(SOURCE_LABELS) as MoneySource[]).map((s) => (
                    <Chip
                      key={s}
                      label={SOURCE_LABELS[s]}
                      colour={SOURCE_COLOURS[s]}
                      count={sourceCounts.get(s) || 0}
                      active={sources.size === 0 || sources.has(s)}
                      onClick={() => toggleIn(sources, s, setSources)}
                    />
                  ))}
                </div>
              </div>

              {activeCategories.length > 0 && (
                <div className="col-12">
                  <div className="fs-8 text-muted text-uppercase fw-semibold mb-1">Category</div>
                  <div className="d-flex flex-wrap gap-1">
                    {activeCategories.map((c) => (
                      <Chip
                        key={c.id}
                        label={c.label}
                        colour={c.colour}
                        active={categories.size === 0 || categories.has(c.id)}
                        onClick={() => toggleIn(categories, c.id, setCategories)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {filterCount > 0 && (
                <div className="col-12">
                  <button className="btn btn-sm btn-light-danger" onClick={clearFilters}>
                    <i className="bi bi-x-lg me-1" /> Clear all filters
                    {mutedIds.size > 0 && ` (including ${mutedIds.size} muted item${mutedIds.size === 1 ? '' : 's'})`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------ empty state ----- */}
      {nothingYet && (
        <div className="card mb-4">
          <div className="card-body text-center py-10">
            <i className="bi bi-graph-up-arrow fs-3x text-muted opacity-50 d-block mb-3" />
            <h4>Nothing to forecast yet</h4>
            <p className="text-muted mb-4">
              Add the bills that come out every month and the income that comes in, and this page will show you
              how the month actually plays out — including the day the balance gets lowest.
            </p>
            <button className="btn btn-primary me-2" onClick={() => setShowImport(true)}>
              <i className="bi bi-upload me-1" /> Import a list
            </button>
            <button className="btn btn-light" onClick={openAdd}>
              <i className="bi bi-plus-lg me-1" /> Add one by hand
            </button>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ summary --------- */}
      <div className="row g-4 mb-4">
        <SummaryCard
          label="Money in"
          value={money(result.totalIn)}
          tone="success"
          icon="bi-arrow-up-right"
          sub={`${result.bySource.filter((s) => s.direction === 'in').reduce((n, s) => n + s.count, 0)} payments`}
        />
        <SummaryCard
          label="Money out"
          value={money(result.totalOut)}
          tone="danger"
          icon="bi-arrow-down-left"
          sub={`${result.bySource.filter((s) => s.direction === 'out').reduce((n, s) => n + s.count, 0)} payments`}
        />
        <SummaryCard
          label="Net"
          value={money(result.net)}
          tone={result.net >= 0 ? 'success' : 'danger'}
          icon="bi-plus-slash-minus"
          sub={`Ends at ${money(result.closingBalance)}`}
        />
        <SummaryCard
          label="Lowest point"
          value={result.lowestBalance === null ? '—' : money(result.lowestBalance)}
          tone={
            result.lowestBalance === null
              ? 'muted'
              : result.lowestBalance < 0
              ? 'danger'
              : bufferLine !== null && result.lowestBalance < bufferLine
              ? 'warning'
              : 'success'
          }
          icon="bi-graph-down"
          sub={
            result.lowestBalanceDate ? (
              <>
                on {formatDay(result.lowestBalanceDate)}
                {result.lowestBalance !== null && result.lowestBalance < 0 && (
                  <span className="text-danger fw-semibold"> — short</span>
                )}
              </>
            ) : (
              'No movements in range'
            )
          }
        />
      </div>

      {/* ------------------------------------------------ chart ----------- */}
      <div className="card mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">How the money moves</h6>
            <span className="text-muted fs-8">
              Bars are what lands each day; the line is the balance carried forward from{' '}
              {money(openingBalance)}.
            </span>
          </div>
        </div>
        <div className="card-body pt-2">
          <RhythmChart
            days={result.days}
            bufferLine={bufferLine}
            lowestBalance={result.lowestBalance}
            lowestBalanceDate={result.lowestBalanceDate}
            showBalance={showBalance}
          />
        </div>
      </div>

      <div className="row g-4">
        {/* -------------------------------------------- day-by-day -------- */}
        <div className="col-12 col-xl-7">
          <div className="card h-100">
            <div className="card-header py-3">
              <h6 className="mb-0">Day by day</h6>
              <span className="text-muted fs-8">
                {daysWithActivity.length} day{daysWithActivity.length === 1 ? '' : 's'} with something on them
              </span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 620, overflowY: 'auto' }}>
              {daysWithActivity.length === 0 ? (
                <div className="text-center text-muted py-10">
                  Nothing scheduled in this range
                  {filterCount > 0 && (
                    <>
                      {' '}
                      —{' '}
                      <button className="btn btn-link btn-sm p-0 align-baseline" onClick={clearFilters}>
                        clear the filters
                      </button>
                    </>
                  )}
                </div>
              ) : (
                daysWithActivity.map((day) => (
                  <div key={day.date} className="border-bottom px-4 py-3">
                    <div className="d-flex justify-content-between align-items-baseline mb-2">
                      <div className="fw-bold">{formatDay(day.date)}</div>
                      <div className="d-flex gap-3 fs-8">
                        {day.inflow > 0 && <span className="text-success">+{money(day.inflow)}</span>}
                        {day.outflow > 0 && <span className="text-danger">−{money(day.outflow)}</span>}
                        <span className={`fw-semibold ${day.balance < 0 ? 'text-danger' : 'text-muted'}`}>
                          {money(day.balance)}
                        </span>
                      </div>
                    </div>
                    {groupSpread(day.occurrences).map((o) => (
                      <div key={o.id} className="d-flex justify-content-between align-items-center py-1">
                        <div className="d-flex align-items-center gap-2 min-w-0">
                          <span
                            className="rounded-circle flex-shrink-0"
                            style={{ width: 8, height: 8, background: getCategoryColour(o.category) }}
                          />
                          <span className="text-truncate">{o.label}</span>
                          {o.isSpread && (
                            <span className="badge badge-light fs-9" title="A monthly budget spread evenly across the days">
                              daily share
                            </span>
                          )}
                          {o.certainty === 'estimated' && !o.isSpread && (
                            <span className="badge badge-light-warning fs-9" title="Our projection, not a date any system has confirmed">
                              projected
                            </span>
                          )}
                          {o.source !== 'manual' && (
                            <span
                              className="badge fs-9"
                              style={{ background: `${SOURCE_COLOURS[o.source]}1a`, color: SOURCE_COLOURS[o.source] }}
                            >
                              {SOURCE_LABELS[o.source]}
                            </span>
                          )}
                        </div>
                        <div className={`fw-semibold flex-shrink-0 ps-2 ${o.direction === 'in' ? 'text-success' : 'text-danger'}`}>
                          {o.direction === 'in' ? '+' : '−'}
                          {money(o.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* -------------------------------------------- items ------------- */}
        <div className="col-12 col-xl-5">
          <div className="card h-100">
            <div className="card-header py-3 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">What repeats</h6>
                <span className="text-muted fs-8">Normalised to a monthly figure · click to edit</span>
              </div>
              <button className="btn btn-sm btn-light-primary" onClick={openAdd}>
                <i className="bi bi-plus-lg" />
              </button>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 620, overflowY: 'auto' }}>
              {rankedItems.length === 0 ? (
                <div className="text-center text-muted py-10 px-4">
                  No personal items yet. The business side above still comes from your recurring invoices and
                  Stripe plans.
                </div>
              ) : (
                rankedItems.map(({ item, monthly }) => {
                  const muted = mutedIds.has(item.id);
                  const dimmed = !item.active || muted;
                  return (
                    <div
                      key={item.id}
                      className={`d-flex align-items-center gap-2 px-4 py-2 border-bottom ${dimmed ? 'opacity-50' : ''}`}
                    >
                      <button
                        className="btn btn-icon btn-sm btn-light flex-shrink-0"
                        title={muted ? 'Put it back in the forecast' : 'Leave it out — just to see what happens'}
                        onClick={() => toggleMute(item.id)}
                      >
                        <i className={`bi ${muted ? 'bi-eye-slash' : 'bi-eye'}`} />
                      </button>
                      <button
                        className="btn btn-link text-start p-0 flex-grow-1 min-w-0 text-decoration-none"
                        onClick={() => openEdit(item)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle flex-shrink-0"
                            style={{ width: 8, height: 8, background: getCategoryColour(item.category) }}
                          />
                          <span className="fw-semibold text-gray-800 text-truncate">{item.name}</span>
                          {!item.active && <span className="badge badge-light fs-9">off</span>}
                        </div>
                        <div className="text-muted fs-8 text-truncate">
                          {describeSchedule(item)} · {getCategoryLabel(item.category)} · {SCOPE_LABELS[item.scope]}
                        </div>
                      </button>

                      {/* Muting is a session what-if and turning something off
                          is a decision. Offering the second one right after the
                          first is the moment it's actually wanted: you muted
                          it, you saw the month without it, now you can commit —
                          without hunting for the switch inside the modal. */}
                      {muted && item.active && (
                        <button
                          className="btn btn-sm btn-light-danger py-1 px-2 fs-9 flex-shrink-0"
                          title="Stop counting this permanently — the record is kept, so you can switch it back on"
                          onClick={() => persistOff(item)}
                        >
                          Turn off
                        </button>
                      )}
                      {!item.active && (
                        <button
                          className="btn btn-sm btn-light-success py-1 px-2 fs-9 flex-shrink-0"
                          title="Count this in the forecast again"
                          onClick={() => persistOn(item)}
                        >
                          Turn on
                        </button>
                      )}
                      <div className="text-end flex-shrink-0">
                        <div className={`fw-semibold ${item.direction === 'in' ? 'text-success' : ''}`}>
                          {item.direction === 'in' ? '+' : '−'}
                          {moneyShort(monthly)}
                        </div>
                        <div className="text-muted fs-9">per month</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {rankedItems.length > 0 && (
              <div className="card-footer py-2 px-4 d-flex justify-content-between align-items-center">
                <span className="text-muted fs-8">
                  {items.filter((i) => i.active).length} of {items.length} on
                </span>
                <button className="btn btn-sm btn-light" onClick={() => setShowImport(true)}>
                  <i className="bi bi-upload me-1" /> Import more
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------ breakdown ------- */}
      {result.byCategory.length > 0 && (
        <div className="row g-4 mt-0">
          <div className="col-12 col-lg-6">
            <BreakdownCard
              title="Where it goes"
              rows={result.byCategory.filter((c) => c.direction === 'out')}
              total={result.totalOut}
              labelOf={(r) => getCategoryLabel(r.category)}
              colourOf={(r) => getCategoryColour(r.category)}
            />
          </div>
          <div className="col-12 col-lg-6">
            <BreakdownCard
              title="Where it comes from"
              rows={result.bySource.filter((s) => s.direction === 'in')}
              total={result.totalIn}
              labelOf={(r) => SOURCE_LABELS[r.source as MoneySource]}
              colourOf={(r) => SOURCE_COLOURS[r.source as MoneySource]}
            />
          </div>
        </div>
      )}

      <RecurringItemModal show={showItemModal} onHide={() => setShowItemModal(false)} editing={editing} />
      <ImportItemsModal show={showImport} onHide={() => setShowImport(false)} existingItems={items} />
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Collapse spread occurrences into a single row per source.
 *
 *  A spread item produces one occurrence per day, which is right for the chart
 *  and wrong for a list — the day view would show "Groceries $32.26" thirty
 *  times over. The daily share is still the honest number, so it's shown as
 *  one row with a badge rather than hidden. */
function groupSpread(occurrences: RhythmOccurrence[]): RhythmOccurrence[] {
  const seen = new Set<string>();
  const out: RhythmOccurrence[] = [];
  for (const o of occurrences) {
    if (!o.isSpread) {
      out.push(o);
      continue;
    }
    const key = `${o.source}:${o.sourceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(o);
  }
  return out;
}

function BreakdownCard<T extends { total: number; count: number }>({
  title,
  rows,
  total,
  labelOf,
  colourOf,
}: {
  title: string;
  rows: T[];
  total: number;
  labelOf: (r: T) => string;
  colourOf: (r: T) => string;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="card h-100">
      <div className="card-header py-3">
        <h6 className="mb-0">{title}</h6>
      </div>
      <div className="card-body">
        {rows.map((r, i) => {
          const pct = total > 0 ? (r.total / total) * 100 : 0;
          return (
            <div key={i} className="mb-3">
              <div className="d-flex justify-content-between align-items-baseline mb-1">
                <div className="d-flex align-items-center gap-2">
                  <span className="rounded" style={{ width: 10, height: 10, background: colourOf(r) }} />
                  <span className="fw-semibold">{labelOf(r)}</span>
                  <span className="text-muted fs-8">({r.count})</span>
                </div>
                <div className="fw-semibold">
                  {money(r.total)}
                  <span className="text-muted fs-8 ms-2">{pct.toFixed(0)}%</span>
                </div>
              </div>
              <div className="progress" style={{ height: 6 }}>
                <div className="progress-bar" style={{ width: `${pct}%`, background: colourOf(r) }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
